import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { formatLocation } from "@/data/schoolData";

function imageMap(value) {
  const map = {};
  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (item?.key) map[item.key] = item?.url || item?.src || "";
    });
  } else if (value && typeof value === "object") {
    Object.entries(value).forEach(([key, item]) => {
      map[key] = typeof item === "string" ? item : item?.url || item?.src || "";
    });
  }
  return map;
}

function imageFrom(item, images, fallback = "") {
  const candidate = typeof item === "string" ? item : item?.url || item?.src || "";
  const resolved = images[candidate] || candidate;
  if (!resolved || typeof resolved !== "string" || resolved.startsWith("data:") || !/^(?:https?:\/\/|\/)/i.test(resolved)) return fallback;
  return resolved;
}

function PreviewImage({ src, alt, testId, className = "" }) {
  if (!src) return <div className={`${className} generated-image-placeholder`} data-testid={`${testId}-placeholder`} aria-hidden="true" />;
  return <img className={className} src={src} alt={alt} data-testid={testId} />;
}

function mergePrograms(items) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => item && typeof item === "object").map((item, index) => ({
    ...item,
    slug: item.slug || `program-${index + 1}`,
    name: item.name || `Program ${index + 1}`,
    kicker: item.kicker || "Program",
    summary: item.summary || "",
  }));
}

function PreviewHeader({ name, branding, logoImage, brandMark }) {
  return <header className="generated-header"><Link to="/" className="generated-brand" data-testid="generated-brand-link">{logoImage ? <img className="generated-brand-logo" src={logoImage} alt={branding.logoAlt || `${name} logo`} data-testid="generated-brand-logo" /> : <span data-testid="generated-brand-mark">{brandMark}</span>}{!branding.logoContainsName && <strong>{name}</strong>}</Link><Link to="/free-week" className="generated-header-cta" data-testid="generated-header-free-week-link">Get Your Free Week Pass</Link></header>;
}

function PreviewHero({ name, location, description, heroImage }) {
  return <section className="generated-hero" data-testid="generated-hero"><div className="generated-hero-image"><PreviewImage src={heroImage} alt={`${name} students practicing martial arts`} testId="generated-hero-image" /></div><div className="generated-hero-copy"><span className="eyebrow">{location ? `${location} · ` : ""}Beginners welcome</span><h1>Build confidence that goes beyond martial arts.</h1><p>{description}</p><Link to="/free-week" className="button button-accent" data-testid="generated-hero-free-week-link">Get Your Free Week Pass <ArrowRight size={16} aria-hidden="true" /></Link><span className="generated-risk">No experience needed. No obligation.</span></div></section>;
}

function PreviewPrograms({ name, offer, programs, images, hasReviews }) {
  return <><section className="generated-proof" data-testid="generated-proof"><strong>{hasReviews ? "PUBLIC REVIEWS" : "SOURCE PREVIEW"}</strong><span>{hasReviews ? "Public reviews from the school source" : "Programs from public school information"}</span><small>Preview · {offer}</small></section><section className="generated-programs" id="programs" data-testid="generated-programs"><div className="generated-section-heading"><span className="eyebrow">Find your fit</span><h2>Programs built around real life.</h2></div><div className="generated-program-grid">{programs.map((program) => <article key={program.slug} className="generated-program" data-testid={`generated-program-${program.slug}`}><PreviewImage src={imageFrom(program.image, images)} alt={`${program.name} at ${name}`} testId={`generated-program-${program.slug}-image`} /><div><span>{program.kicker}</span><h3>{program.name}</h3>{program.summary && <p>{program.summary}</p>}</div></article>)}</div></section></>;
}

function PreviewFinalCta() {
  return <section className="generated-cta" data-testid="generated-final-cta"><span className="eyebrow">A low-pressure first step</span><h2>See what a Free Week can change.</h2><Link to="/free-week" className="button button-accent" data-testid="generated-final-free-week-link">Get Your Free Week Pass <ArrowRight size={16} aria-hidden="true" /></Link></section>;
}

function PreviewFooter({ name, location }) {
  return <footer className="generated-footer" data-testid="generated-footer"><span data-testid="generated-preview-business-name">{name}</span>{location && <span data-testid="generated-preview-location">{location}</span>}<span data-testid="generated-preview-status">Preview · noindex</span></footer>;
}


export default function PreviewPage() {
  const { siteId } = useParams();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const existingRobots = document.querySelector('meta[name="robots"]');
    const previousRobots = existingRobots?.getAttribute("content");
    const robots = existingRobots || document.createElement("meta");
    if (!existingRobots) {
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "noindex,nofollow,noarchive";
    const previousTitle = document.title;
    const existingDescription = document.querySelector('meta[name="description"]');
    const previousDescription = existingDescription?.getAttribute("content");
    const description = existingDescription || document.createElement("meta");
    if (!existingDescription) {
      description.name = "description";
      document.head.appendChild(description);
    }
    const businessName = config?.business?.name || "School preview";
    const sourcePrograms = (config?.programs || []).map((item) => item?.name).filter(Boolean).slice(0, 4);
    const metadataDescription = config?.seo?.defaultDescription || config?.seo?.description || (sourcePrograms.length ? `${businessName} public programs: ${sourcePrograms.join(", ")}.` : `Public information from ${businessName}.`);
    document.title = config ? `${businessName} | Preview` : "School preview";
    description.content = metadataDescription;
    return () => {
      if (existingRobots) existingRobots.setAttribute("content", previousRobots || "");
      else robots.remove();
      document.title = previousTitle;
      if (existingDescription) existingDescription.setAttribute("content", previousDescription || "");
      else description.remove();
    };
  }, [config]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/public/sites/${siteId}`);
        if (!response.ok) throw new Error("Preview unavailable");
        setConfig(await response.json());
      } catch {
        setError("This preview is not available yet.");
      }
    };
    load();
  }, [siteId]);

  if (error) return <div className="preview-state" data-testid="preview-error">{error}</div>;
  if (!config) return <div className="preview-state" data-testid="preview-loading">Loading preview…</div>;

  const business = config.business || {};
  const branding = config.branding || {};
  const name = business.name || "Martial arts school preview";
  const location = business.locationVerified === true ? formatLocation({ city: business.city, state: business.state, stateCode: business.stateCode }) : "";
  const images = imageMap(config.images);
  const heroImage = imageFrom("hero", images);
  const logoImage = imageFrom(branding.logoUrl, images);
  const brandMark = branding.monogram || name.split(/\s+/).filter(Boolean).map((word) => word[0]).slice(0, 3).join("").toUpperCase() || "MA";
  const actionColor = /^#[0-9a-f]{6}$/i.test(branding.actionColor || "") ? branding.actionColor : /^#[0-9a-f]{6}$/i.test(branding.accentColor || "") ? branding.accentColor : "#b6202a";
  const previewStyle = actionColor ? { "--generated-action": actionColor } : undefined;
  const programs = mergePrograms(config.programs);
  const sourceProgramNames = programs.map((program) => program.name).filter(Boolean).slice(0, 4);
  const offer = config.offer?.name || "Free Week Pass";
  const description = config.seo?.description || config.seo?.defaultDescription || (sourceProgramNames.length ? `${name} offers ${sourceProgramNames.join(", ")}.` : `Explore the public information shared by ${name}.`);

  return (
    <div className="generated-preview" data-testid="generated-preview" style={previewStyle}>
      <PreviewHeader name={name} branding={branding} logoImage={logoImage} brandMark={brandMark} />
      <main>
        <PreviewHero name={name} location={location} description={description} heroImage={heroImage} />
        <PreviewPrograms name={name} offer={offer} programs={programs} images={images} hasReviews={Array.isArray(config.reviews) && config.reviews.length > 0} />
        <PreviewFinalCta />
      </main>
      <PreviewFooter name={name} location={location} />
    </div>
  );
}
